// 內建英漢字典：查字、變化形還原、前綴建議、中文反查（資料由 scripts/build-dict.mjs 產生）
export type Entry = {
  w: string; pos: string; zh: string; ex: string; exZh: string;
  noun?: 'C' | 'U' | 'CU' | 'P';          // 可數／不可數／兩者／複數名詞
  past?: string; pp?: string;             // 動詞過去式、過去分詞
  topic: string;
};
export type RawDict = { t: string[]; r: [string, string, string, string, string, string, number][] };
export type Dict = { byWord: Map<string, Entry[]>; words: string[]; forms: Map<string, string>; entries: Entry[] };

export const hasCjk = (s: string) => /[㐀-鿿]/.test(s);
export const normalize = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();

export function buildDict(raw: RawDict): Dict {
  const byWord = new Map<string, Entry[]>();
  const forms = new Map<string, string>();
  const entries: Entry[] = [];
  for (const [w, pos, zh, ex, exZh, gram, ti] of raw.r) {
    const e: Entry = { w, pos, zh, ex, exZh, topic: raw.t[ti] };
    for (const part of gram.split(';')) {
      if (/^(C|U|CU|P)$/.test(part)) e.noun = part as Entry['noun'];
      else if (part.includes('/')) { [e.past, e.pp] = part.split('/'); }
    }
    const pl = zh.match(/複數\s*([A-Za-z]+)/);   // 例：男人（複數 men）
    const key = w.toLowerCase();
    if (pl) forms.set(pl[1].toLowerCase(), key);
    if (e.past) forms.set(e.past.toLowerCase(), key);
    if (e.pp) forms.set(e.pp.toLowerCase(), key);
    byWord.set(key, [...(byWord.get(key) ?? []), e]);
    entries.push(e);
  }
  // 變化形與原字相同、或本身就是字庫中的字時（put、cut 等）不另外建立
  forms.forEach((lemma, f) => { if (f === lemma || byWord.has(f)) forms.delete(f); });
  return { byWord, words: Array.from(byWord.keys()).sort(), forms, entries };
}

const VOWEL = /[aeiou]/;
// 規則變化的還原候選（由可能性高到低）；呼叫端要再確認候選存在於字庫
export function candidates(w: string): string[] {
  const out: string[] = [];
  const add = (s: string) => { if (s.length >= 2 && s !== w && !out.includes(s)) out.push(s); };
  const undouble = (s: string) => (s.length > 2 && s[s.length - 1] === s[s.length - 2] ? s.slice(0, -1) : '');
  if (w.endsWith('ies')) add(w.slice(0, -3) + 'y');
  if (w.endsWith('ied')) add(w.slice(0, -3) + 'y');
  if (w.endsWith('ier')) add(w.slice(0, -3) + 'y');
  if (w.endsWith('iest')) add(w.slice(0, -4) + 'y');
  if (w.endsWith('ily')) add(w.slice(0, -3) + 'y');
  if (w.endsWith('es')) { add(w.slice(0, -2)); add(w.slice(0, -1)); }
  if (w.endsWith('s') && !w.endsWith('ss')) add(w.slice(0, -1));
  if (w.endsWith('ed')) { const s = w.slice(0, -2); add(s); add(s + 'e'); add(undouble(s)); }
  if (w.endsWith('ing')) { const s = w.slice(0, -3); if (VOWEL.test(s)) { add(s); add(s + 'e'); add(undouble(s)); } }
  if (w.endsWith('est')) { const s = w.slice(0, -3); add(s); add(s + 'e'); add(undouble(s)); }
  if (w.endsWith('er')) { const s = w.slice(0, -2); add(s); add(s + 'e'); add(undouble(s)); }
  if (w.endsWith('ly')) add(w.slice(0, -2));
  return out;
}

export type Found = { entries: Entry[]; lemma: string; via?: string };   // via：使用者輸入的變化形
export function lookup(d: Dict, input: string): Found | null {
  const w = normalize(input);
  if (!w) return null;
  const hit = d.byWord.get(w);
  if (hit) return { entries: hit, lemma: w };
  const irregular = d.forms.get(w);
  if (irregular && d.byWord.has(irregular)) return { entries: d.byWord.get(irregular)!, lemma: irregular, via: w };
  for (const c of candidates(w)) {
    const e = d.byWord.get(c);
    if (e) return { entries: e, lemma: c, via: w };
  }
  return null;
}

// 輸入到一半時的建議：前綴相符的字（含片語），最多 n 個
export function suggest(d: Dict, input: string, n = 8): string[] {
  const w = normalize(input);
  if (!w) return [];
  const out: string[] = [];
  for (const k of d.words) { if (k.startsWith(w) && k !== w) { out.push(k); if (out.length >= n) break; } }
  return out;
}

// 中文反查：中文欄含有輸入的字，中文欄越短（越貼近）越前面
export function searchZh(d: Dict, input: string, n = 20): Entry[] {
  const q = input.trim();
  if (!q) return [];
  return d.entries.filter(e => e.zh.includes(q)).sort((a, b) => a.zh.length - b.zh.length).slice(0, n);
}

export const NOUN_LABEL: Record<NonNullable<Entry['noun']>, string> = { C: '可數', U: '不可數', CU: '可數／不可數', P: '複數名詞' };
